/*
 * Copyright 2026 Sony Corporation
 */

import { grid } from './grid-scene';
import type { SceneEditor } from './scene-editor';
import type { SceneInstance, SceneSource } from './types';

export type SwitchStatus = 'idle' | 'loading' | 'failed';

export interface SwitchState {
  status: SwitchStatus;
  /** The scene on screen, which during a switch is not the one being loaded. */
  activeId: string | undefined;
  pendingId: string | undefined;
  error: Error | undefined;
}

export interface SceneSwitcher {
  readonly active: SceneInstance | undefined;
  readonly state: SwitchState;
  switchTo: (source: SceneSource) => Promise<void>;
  subscribe: (listener: (state: SwitchState) => void) => () => void;
  dispose: () => void;
}

export interface SceneSwitcherOptions {
  editor: SceneEditor;
  /** Rendered when a switch fails with nothing already on screen. */
  fallback?: SceneSource;
}

/** Warming has to be bounded: a hitch on commit beats an editor that never returns. */
const WARM_BUDGET_MS = 1500;

/**
 * Load, then commit (scene-switch.md §7).
 *
 * Each rule here exists because the obvious implementation breaks something:
 * staging keeps a failed switch harmless, the generation counter stops a fast
 * double-click leaking a container, and the deferred dispose keeps the replaced
 * scene alive through the frame already in flight.
 */
export const createSceneSwitcher = ({ editor, fallback = grid }: SceneSwitcherOptions): SceneSwitcher => {
  let active: SceneInstance | undefined;
  let generation = 0;
  let inFlight: AbortController | undefined;
  let disposed = false;

  let state: SwitchState = { status: 'idle', activeId: undefined, pendingId: undefined, error: undefined };
  const listeners = new Set<(next: SwitchState) => void>();

  const publish = (next: Partial<SwitchState>) => {
    state = { ...state, ...next };
    for (const listener of listeners) {
      listener(state);
    }
  };

  const disposeAfterNextRender = (instance: SceneInstance) => {
    editor.scene.onAfterRenderObservable.addOnce(() => instance.dispose());
  };

  const attempt = async (source: SceneSource, allowFallback: boolean): Promise<void> => {
    if (disposed) {
      return;
    }

    // Already on screen: reloading would drop a frame for no reason.
    if (active?.sceneId === source.id && state.status !== 'failed') {
      return;
    }

    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;

    const mine = ++generation;
    publish({ status: 'loading', pendingId: source.id, error: undefined });

    try {
      const staged = await source.stage({ scene: editor.scene }, controller.signal);

      // Superseded while loading: discard silently. The user has already asked
      // for something else, so a message about this one would be noise.
      if (mine !== generation || disposed) {
        staged.abandon();
        return;
      }

      await staged.warm?.(WARM_BUDGET_MS);
      if (mine !== generation || disposed) {
        staged.abandon();
        return;
      }

      const previous = active;

      // Commit: no await and no allocation from here to the end of the block, so
      // the swap lands in one frame.
      editor.scene.blockMaterialDirtyMechanism = true;
      try {
        active = staged.commit();
        editor.frame(active.bounds, active.defaultView);
        editor.setShadowCasters(active.shadowCasters);
      } finally {
        editor.scene.blockMaterialDirtyMechanism = false;
      }

      if (previous) {
        disposeAfterNextRender(previous);
      }

      publish({ status: 'idle', activeId: active.sceneId, pendingId: undefined, error: undefined });
    } catch (cause) {
      if (mine !== generation || disposed) {
        return;
      }

      const error = cause instanceof Error ? cause : new Error('Could not load the scene.');
      publish({ status: 'failed', pendingId: undefined, error });

      // The previous scene is still on screen and untouched, so there is only
      // something to fall back to when nothing was showing yet.
      if (!active && allowFallback && source.id !== fallback.id) {
        await attempt(fallback, false);
        publish({ status: 'failed', error });
      }
    }
  };

  return {
    get active() {
      return active;
    },
    get state() {
      return state;
    },

    switchTo: (source) => attempt(source, true),

    subscribe: (listener) => {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },

    dispose: () => {
      disposed = true;
      inFlight?.abort();
      editor.setShadowCasters(undefined);
      active?.dispose();
      active = undefined;
      listeners.clear();
    },
  };
};
